<template>
  <div class="vlm-analyzer px-1 px-md-2">
    <b-row class="pt-2 pb-1 align-items-center">
      <b-col cols="12" md="8">
        <h2 class="mb-25">{{ $t('vlm_analyzer.title') }} · {{ pageTitle }}</h2>
      </b-col>
      <b-col cols="12" md="4" class="text-md-right mt-1 mt-md-0">
        <b-button v-if="isCameras && canCreate" variant="primary" @click="openCamera()">
          <feather-icon icon="PlusIcon" class="mr-50" />{{ $t('vlm_analyzer.add_camera') }}
        </b-button>
        <b-button v-else variant="outline-primary" @click="load">
          <feather-icon icon="RefreshCwIcon" class="mr-50" />{{ $t('vlm_analyzer.refresh') }}
        </b-button>
      </b-col>
    </b-row>

    <b-alert v-if="error" show variant="danger" class="mb-2">{{ error }}</b-alert>

    <b-card v-if="isCameras" no-body>
      <b-table :items="cameras" :fields="cameraFields" responsive primary-key="id" :busy="loading" show-empty :empty-text="$t('vlm_analyzer.no_cameras')">
        <template #cell(name)="{ item }">
          <strong>{{ item.name }}</strong><small class="d-block text-muted">Proxy #{{ item.proxy_id }}</small>
        </template>
        <template #cell(detection_status)="{ item }">
          <div v-for="detection in item.detections" :key="detection.id" class="mb-25">
            <b-badge :variant="detection.enabled ? 'light-success' : 'light-secondary'">{{ detection.enabled ? $t('vlm_analyzer.enabled') : $t('vlm_analyzer.paused') }}</b-badge>
          </div>
        </template>
        <template #cell(detections)="{ item }">
          <div v-for="detection in item.detections" :key="detection.id" class="mb-25">
            <b-badge variant="light-primary">{{ eventLabel(detection.event_type) }}</b-badge>
          </div>
        </template>
        <template #cell(created_at)="{ item }">{{ formatDate(item.created_at) }}</template>
        <template #cell(actions)="{ item }">
          <b-button size="sm" variant="flat-primary" @click="openAnalytics(item)">{{ $t('vlm_analyzer.manage') }}</b-button>
          <b-button v-if="canUpdate" size="sm" variant="flat-secondary" @click="openCamera(item)"><feather-icon icon="EditIcon" /></b-button>
          <b-button v-if="canDelete" size="sm" variant="flat-danger" @click="removeCamera(item)"><feather-icon icon="TrashIcon" /></b-button>
        </template>
      </b-table>
    </b-card>

    <template v-else>
      <b-card class="mb-1">
        <b-row>
          <b-col cols="12" md="5"><b-form-input v-model="filters.search" :placeholder="$t('vlm_analyzer.search')" @keyup.enter="load" /></b-col>
          <b-col cols="6" md="3"><b-form-select v-model="filters.event_type" :options="eventOptions" @change="load" /></b-col>
          <b-col cols="6" md="3"><b-form-select v-model="filters.vlm_provider" :options="providerOptions" @change="load" /></b-col>
          <b-col cols="12" md="1" class="mt-1 mt-md-0"><b-button block variant="outline-primary" @click="load"><feather-icon icon="SearchIcon" /></b-button></b-col>
        </b-row>
      </b-card>
      <b-card no-body>
        <b-table :items="monitoring.items" :fields="monitoringFields" responsive primary-key="id" :busy="loading" show-empty :empty-text="$t('vlm_analyzer.no_results')">
          <template #cell(name)="{ item }"><strong>{{ item.name }}</strong><small class="d-block text-muted">Proxy #{{ item.proxy_id }}</small></template>
          <template #cell(detections)="{ item }">
            <div v-for="detection in item.detections" :key="detection.id" class="mb-50">
              <b-badge :variant="detection.enabled ? 'light-success' : 'light-secondary'">{{ eventLabel(detection.event_type) }}</b-badge>
              <span class="ml-50">{{ latestResult(detection) }}</span>
              <b-button v-if="latestAnalysis(detection) && latestAnalysis(detection).snapshot_available" size="sm" variant="flat-secondary" class="float-right" @click="showSnapshot(item, detection)"><feather-icon icon="ImageIcon" /></b-button>
              <b-button v-if="canAnalyze" size="sm" variant="flat-primary" class="float-right" @click="analyze(item, detection)">{{ $t('vlm_analyzer.analyze') }}</b-button>
            </div>
          </template>
        </b-table>
      </b-card>
    </template>

    <b-modal v-model="cameraModal" :title="editingCamera ? $t('vlm_analyzer.edit_camera') : $t('vlm_analyzer.add_camera')" hide-footer>
      <b-form-group :label="$t('vlm_analyzer.select_camera')">
        <CamerasProxySelect v-model="cameraForm.proxy_id" :disabled="Boolean(editingCamera)" :reduce="camera => camera.value" @options-loaded="rememberCameraOptions" />
      </b-form-group>
      <b-form-group :label="$t('vlm_analyzer.camera_name')"><b-form-input v-model.trim="cameraForm.name" maxlength="120" /></b-form-group>
      <b-form-group :label="$t('vlm_analyzer.language')"><b-form-select v-model="cameraForm.language" :options="languageOptions" /></b-form-group>
      <b-form-checkbox v-model="cameraForm.enabled">{{ $t('vlm_analyzer.camera_enabled') }}</b-form-checkbox>
      <template v-if="!editingCamera">
        <label>{{ $t('vlm_analyzer.analytics') }}</label>
        <b-form-checkbox-group v-model="cameraForm.events" :options="eventOptions.slice(1)" stacked />
        <b-row class="mt-1 vlm-settings-row">
          <b-col cols="12" md="4"><b-form-group class="mb-0" label-class="vlm-settings-label" :label="$t('vlm_analyzer.provider')"><b-form-select v-model="cameraForm.provider" :options="providerOptions.slice(1)" @change="resetCameraReasoning" /></b-form-group></b-col>
          <b-col cols="12" md="4"><b-form-group class="mb-0" label-class="vlm-settings-label" :label="$t('vlm_analyzer.reasoning')"><b-form-select v-model="cameraForm.reasoning_mode" :options="reasoningOptions(cameraForm.provider)" /></b-form-group></b-col>
          <b-col cols="12" md="4"><b-form-group class="mb-0" label-class="vlm-settings-label" :label="$t('vlm_analyzer.analysis_interval')"><b-form-input v-model.number="cameraForm.interval" type="number" :min="intervalLimits.min" :max="intervalLimits.max" /></b-form-group></b-col>
        </b-row>
        <b-form-checkbox v-model="cameraForm.detection_enabled" class="mt-1">{{ $t('vlm_analyzer.detection_enabled') }}</b-form-checkbox>
        <fieldset v-if="cameraForm.events.length" class="border rounded p-1 mt-2">
          <legend class="w-auto px-50 h6">{{ $t('vlm_analyzer.incident_confirmation') }}</legend>
          <b-form-group :label="$t('vlm_analyzer.alert_cooldown')"><b-form-input v-model.number="cameraForm.alarm_cooldown_seconds" type="number" :min="cooldownLimits.min" :max="cooldownLimits.max" /></b-form-group>
          <p class="small text-muted">{{ $t('vlm_analyzer.cooldown_help') }}</p>
          <p class="small text-muted">{{ $t('vlm_analyzer.threshold_help') }}</p>
          <b-row>
            <b-col v-for="category in cameraThresholdCategories" :key="category" cols="12" md="6"><b-form-group :label="thresholdLabel(category)"><b-form-input v-model.number="cameraForm.alarm_thresholds_seconds[category]" type="number" :min="thresholdLimits.min" :max="thresholdLimits.max" /></b-form-group></b-col>
          </b-row>
        </fieldset>
      </template>
      <div class="text-right mt-2"><b-button variant="outline-secondary" class="mr-1" @click="cameraModal = false">{{ $t('vlm_analyzer.cancel') }}</b-button><b-button variant="primary" :disabled="saving || !cameraForm.proxy_id || !cameraForm.name" @click="saveCamera">{{ $t('vlm_analyzer.save') }}</b-button></div>
    </b-modal>

    <b-modal v-model="analyticsModal" :title="$t('vlm_analyzer.analytics')" title-class="text-white" hide-footer>
      <div v-for="detection in managedCamera.detections" :key="detection.id" class="d-flex align-items-center border-bottom py-1">
        <div class="flex-grow-1">
          <strong>{{ eventLabel(detection.event_type) }}</strong>
          <small class="d-block text-muted">{{ detection.vlm_provider }} · {{ detection.analysis_interval_seconds }} s</small>
          <b-badge variant="light-secondary" class="mt-25">{{ $t('vlm_analyzer.reasoning') }}: {{ reasoningLabel(detection.reasoning_mode) }}</b-badge>
        </div>
        <b-button v-if="canUpdate" size="sm" variant="flat-secondary" @click="openDetection(detection)"><feather-icon icon="EditIcon" /></b-button>
        <b-button v-if="canUpdate" size="sm" :variant="detection.enabled ? 'flat-success' : 'flat-secondary'" :title="detection.enabled ? $t('vlm_analyzer.pause_detection') : $t('vlm_analyzer.enable_detection')" @click="toggleDetection(detection)">{{ detection.enabled ? $t('vlm_analyzer.enabled') : $t('vlm_analyzer.paused') }}</b-button>
      </div>
      <div class="text-right mt-2"><b-button v-if="canCreate" variant="primary" :disabled="!availableEvents.length" @click="openDetection()">{{ $t('vlm_analyzer.add_analytic') }}</b-button></div>
    </b-modal>

    <b-modal v-model="detectionModal" :title="editingDetection ? $t('vlm_analyzer.edit_analytic') : $t('vlm_analyzer.add_analytic')" hide-footer>
      <b-form-group :label="$t('vlm_analyzer.event')"><b-form-select v-model="detectionForm.event_type" :options="detectionEventOptions" :disabled="Boolean(editingDetection)" @change="resetThresholds" /></b-form-group>
      <b-form-group :label="$t('vlm_analyzer.provider')"><b-form-select v-model="detectionForm.vlm_provider" :options="providerOptions.slice(1)" @change="resetReasoning" /></b-form-group>
      <b-form-group :label="$t('vlm_analyzer.reasoning')"><b-form-select v-model="detectionForm.reasoning_mode" :options="reasoningOptions(detectionForm.vlm_provider)" /></b-form-group>
      <b-form-group :label="$t('vlm_analyzer.analysis_interval')"><b-form-input v-model.number="detectionForm.analysis_interval_seconds" type="number" :min="intervalLimits.min" :max="intervalLimits.max" /></b-form-group>
      <b-form-checkbox v-model="detectionForm.enabled">{{ $t('vlm_analyzer.detection_enabled') }}</b-form-checkbox>
      <fieldset class="border rounded p-1 mt-2">
        <legend class="w-auto px-50 h6">{{ $t('vlm_analyzer.incident_confirmation') }}</legend>
        <b-form-group :label="$t('vlm_analyzer.alert_cooldown')"><b-form-input v-model.number="detectionForm.alarm_cooldown_seconds" type="number" :min="cooldownLimits.min" :max="cooldownLimits.max" /></b-form-group>
        <p class="small text-muted">{{ $t('vlm_analyzer.cooldown_help') }}</p>
        <p class="small text-muted">{{ $t('vlm_analyzer.threshold_help') }}</p>
        <b-row>
          <b-col v-for="category in thresholdCategories" :key="category" cols="12" md="6"><b-form-group :label="thresholdLabel(category)"><b-form-input v-model.number="detectionForm.alarm_thresholds_seconds[category]" type="number" :min="thresholdLimits.min" :max="thresholdLimits.max" /></b-form-group></b-col>
        </b-row>
      </fieldset>
      <div class="text-right mt-2"><b-button variant="outline-secondary" class="mr-1" @click="detectionModal = false">{{ $t('vlm_analyzer.cancel') }}</b-button><b-button variant="primary" :disabled="saving || !detectionForm.event_type" @click="saveDetection">{{ $t('vlm_analyzer.save') }}</b-button></div>
    </b-modal>

    <b-modal v-model="snapshotModal" title="Snapshot de análisis" hide-footer size="lg" @hidden="clearSnapshot">
      <b-alert v-if="snapshotError" show variant="danger">{{ snapshotError }}</b-alert>
      <img v-if="snapshotImage" :src="snapshotImage" alt="Snapshot de análisis" class="img-fluid rounded border">
    </b-modal>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from '@vue/composition-api'
import { BAlert, BBadge, BButton, BCard, BCol, BFormCheckbox, BFormCheckboxGroup, BFormGroup, BFormInput, BFormSelect, BModal, BRow, BTable } from 'bootstrap-vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import ability from '@/libs/acl/ability'
import CamerasProxySelect from '@/components/CamerasProxySelect.vue'

const emptyCatalogs = () => ({ providers: [], event_types: [], languages: [], defaults: {}, limits: {} })
const providerFor = (catalogs, value) => catalogs.providers.find(provider => provider.value === value) || catalogs.providers[0] || { value: '', reasoning_modes: [] }
const reasoningFor = (catalogs, provider) => {
  const modes = providerFor(catalogs, provider).reasoning_modes || []
  const defaultMode = catalogs.defaults.reasoning_mode
  return modes.includes(defaultMode) ? defaultMode : modes[0] || ''
}
const thresholdsFor = (catalogs, event, values = {}) => {
  const eventType = catalogs.event_types.find(item => item.value === event)
  return (eventType?.threshold_categories || []).reduce((thresholds, category) => ({ ...thresholds, [category]: Number(values[category] ?? 0) }), {})
}
const defaultCamera = catalogs => {
  const provider = providerFor(catalogs, catalogs.defaults.provider)
  const event = catalogs.event_types[0]?.value || ''

  return {
    proxy_id: null,
    name: '',
    language: catalogs.defaults.language || catalogs.languages[0]?.value || '',
    enabled: true,
    events: event ? [event] : [],
    provider: provider.value,
    reasoning_mode: reasoningFor(catalogs, provider.value),
    interval: Number(catalogs.defaults.analysis_interval_seconds || 30),
    detection_enabled: true,
    alarm_cooldown_seconds: Number(catalogs.defaults.alarm_cooldown_seconds || 0),
    alarm_thresholds_seconds: thresholdsFor(catalogs, event),
  }
}
const defaultDetection = (catalogs, event = '') => {
  const provider = providerFor(catalogs, catalogs.defaults.provider)

  return {
    event_type: event,
    vlm_provider: provider.value,
    reasoning_mode: reasoningFor(catalogs, provider.value),
    analysis_interval_seconds: Number(catalogs.defaults.analysis_interval_seconds || 30),
    enabled: true,
    alarm_cooldown_seconds: Number(catalogs.defaults.alarm_cooldown_seconds || 0),
    alarm_thresholds_seconds: thresholdsFor(catalogs, event),
  }
}
const sortCamerasDescending = cameras => [...cameras].sort((left, right) => Number(right.proxy_id || 0) - Number(left.proxy_id || 0))

export default {
  name: 'VlmAnalyzer',
  components: { BAlert, BBadge, BButton, BCard, BCol, BFormCheckbox, BFormCheckboxGroup, BFormGroup, BFormInput, BFormSelect, BModal, BRow, BTable, CamerasProxySelect },
  props: { section: { type: String, required: true } },
  setup(props, { root }) {
    const toast = useToast()
    const catalogs = ref(emptyCatalogs())
    const cameras = ref([])
    const monitoring = ref({ items: [] })
    const loading = ref(false)
    const saving = ref(false)
    const error = ref('')
    const cameraModal = ref(false)
    const analyticsModal = ref(false)
    const editingCamera = ref(null)
    const managedCamera = ref({ detections: [] })
    const cameraForm = ref(defaultCamera(catalogs.value))
    const detectionForm = ref(defaultDetection(catalogs.value))
    const detectionModal = ref(false)
    const editingDetection = ref(null)
    const cameraOptions = ref([])
    const selectedCameraOptions = ref([])
    const snapshotModal = ref(false)
    const snapshotImage = ref('')
    const snapshotError = ref('')
    const filters = ref({ search: '', event_type: '', vlm_provider: '' })
    const isCameras = computed(() => props.section === 'cameras')
    const pageTitle = computed(() => root.$t(`vlm_analyzer.${props.section}`))
    const canCreate = computed(() => ability.can('create', 'VlmAnalyzer'))
    const canUpdate = computed(() => ability.can('update', 'VlmAnalyzer'))
    const canDelete = computed(() => ability.can('delete', 'VlmAnalyzer'))
    const canAnalyze = computed(() => ability.can('analyze', 'VlmAnalyzer'))
    const eventOptions = computed(() => [{ value: '', text: root.$t('vlm_analyzer.event') }, ...catalogs.value.event_types.map(event => ({ value: event.value, text: root.$t(`vlm_analyzer.${event.value}`) }))])
    const providerOptions = computed(() => [{ value: '', text: root.$t('vlm_analyzer.provider') }, ...catalogs.value.providers.map(provider => ({ value: provider.value, text: provider.label }))])
    const languageOptions = computed(() => catalogs.value.languages.map(language => ({ value: language.value, text: language.label })))
    const intervalLimits = computed(() => catalogs.value.limits.analysis_interval_seconds || { min: 1, max: 86400 })
    const cooldownLimits = computed(() => catalogs.value.limits.alarm_cooldown_seconds || { min: 0, max: 86400 })
    const thresholdLimits = computed(() => catalogs.value.limits.alarm_threshold_seconds || { min: 0, max: 86400 })
    const cameraFields = computed(() => [{ key: 'name', label: root.$t('vlm_analyzer.cameras') }, { key: 'detection_status', label: root.$t('vlm_analyzer.analytic_status') }, { key: 'detections', label: root.$t('vlm_analyzer.analytics') }, { key: 'created_at', label: root.$t('vlm_analyzer.created_at') }, { key: 'actions', label: root.$t('vlm_analyzer.actions') }])
    const monitoringFields = computed(() => [{ key: 'name', label: root.$t('vlm_analyzer.cameras') }, { key: 'detections', label: root.$t('vlm_analyzer.monitoring') }])
    const availableEvents = computed(() => eventOptions.value.filter(option => option.value && !managedCamera.value.detections.some(detection => detection.event_type === option.value)))
    const detectionEventOptions = computed(() => eventOptions.value.filter(option => option.value && (!editingDetection.value || option.value === editingDetection.value.event_type || !managedCamera.value.detections.some(detection => detection.event_type === option.value))))
    const thresholdCategories = computed(() => catalogs.value.event_types.find(event => event.value === detectionForm.value.event_type)?.threshold_categories || [])
    const cameraThresholdCategories = computed(() => catalogs.value.event_types.find(event => event.value === cameraForm.value.events[0])?.threshold_categories || [])

    const notify = (message, variant = 'success') => toast({ component: ToastificationContent, props: { title: message, icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon', variant } })
    const eventLabel = event => root.$t(`vlm_analyzer.${event}`)
    const thresholdLabel = category => root.$t(`vlm_analyzer.${category}`)
    const reasoningOptions = provider => (providerFor(catalogs.value, provider).reasoning_modes || []).map(value => ({ value, text: root.$t(`vlm_analyzer.reasoning_${value}`) }))
    const reasoningLabel = value => root.$t(`vlm_analyzer.reasoning_${value || 'off'}`)
    const latestAnalysis = detection => detection.analyses?.[0] || null
    const latestResult = detection => latestAnalysis(detection)?.output?.decision || detection.last_error || root.$t('vlm_analyzer.last_analysis')
    const formatDate = value => {
      if (!value) return '—'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString()
    }
    const endpoint = () => isCameras.value ? 'v1/vlm-analyzer/cameras' : 'v1/vlm-analyzer/monitoring'
    const loadCatalogs = async () => {
      const response = await axios.get('v1/vlm-analyzer/catalogs')
      catalogs.value = { ...emptyCatalogs(), ...(response.data.data || {}) }
    }
    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const response = await axios.get(endpoint(), { params: isCameras.value ? {} : filters.value })
        if (isCameras.value) cameras.value = sortCamerasDescending(response.data.data || [])
        else monitoring.value = response.data.data || { items: [] }
      } catch (requestError) {
        error.value = requestError?.response?.data?.message || root.$t('vlm_analyzer.load_error')
      } finally { loading.value = false }
    }
    const openCamera = camera => {
      editingCamera.value = camera || null
      cameraForm.value = camera ? { ...defaultCamera(catalogs.value), ...camera, events: camera.detections.map(detection => detection.event_type) } : defaultCamera(catalogs.value)
      selectedCameraOptions.value = camera ? [{ value: camera.proxy_id, label: camera.name }] : []
      cameraModal.value = true
    }
    const rememberCameraOptions = options => { cameraOptions.value = options || [] }
    const saveCamera = async () => {
      saving.value = true
      try {
        const payload = { name: cameraForm.value.name, proxy_id: Number(cameraForm.value.proxy_id), language: cameraForm.value.language, enabled: cameraForm.value.enabled }
        if (!editingCamera.value) payload.detections = cameraForm.value.events.map(event_type => ({ event_type, vlm_provider: cameraForm.value.provider, reasoning_mode: cameraForm.value.reasoning_mode, analysis_interval_seconds: Number(cameraForm.value.interval), enabled: cameraForm.value.detection_enabled, alarm_thresholds_seconds: thresholdsFor(catalogs.value, event_type, cameraForm.value.alarm_thresholds_seconds), alarm_cooldown_seconds: Number(cameraForm.value.alarm_cooldown_seconds) }))
        await (editingCamera.value ? axios.patch(`v1/vlm-analyzer/cameras/${editingCamera.value.id}`, payload) : axios.post('v1/vlm-analyzer/cameras', payload))
        cameraModal.value = false
        notify(root.$t('vlm_analyzer.saved'))
        await load()
      } catch (requestError) { notify(requestError?.response?.data?.message || root.$t('vlm_analyzer.save_error'), 'danger') } finally { saving.value = false }
    }
    const removeCamera = async camera => {
      if (!window.confirm(`${root.$t('vlm_analyzer.delete')} ${camera.name}?`)) return
      try { await axios.delete(`v1/vlm-analyzer/cameras/${camera.id}`); notify(root.$t('vlm_analyzer.deleted')); await load() } catch (requestError) { notify(root.$t('vlm_analyzer.action_error'), 'danger') }
    }
    const openAnalytics = camera => { managedCamera.value = camera; detectionModal.value = false; editingDetection.value = null; analyticsModal.value = true }
    const resetThresholds = () => { detectionForm.value.alarm_thresholds_seconds = thresholdsFor(catalogs.value, detectionForm.value.event_type) }
    const resetCameraReasoning = () => { cameraForm.value.reasoning_mode = reasoningFor(catalogs.value, cameraForm.value.provider) }
    const resetReasoning = () => { detectionForm.value.reasoning_mode = reasoningFor(catalogs.value, detectionForm.value.vlm_provider) }
    const openDetection = detection => {
      editingDetection.value = detection || null
      detectionForm.value = detection
        ? { ...defaultDetection(catalogs.value, detection.event_type), ...detection, alarm_cooldown_seconds: Number(detection.alarm_cooldown_seconds || 0), alarm_thresholds_seconds: thresholdsFor(catalogs.value, detection.event_type, detection.alarm_thresholds_seconds) }
        : defaultDetection(catalogs.value, availableEvents.value[0]?.value || '')
      detectionModal.value = true
    }
    const saveDetection = async () => {
      saving.value = true
      try {
        const payload = {
          event_type: detectionForm.value.event_type,
          vlm_provider: detectionForm.value.vlm_provider,
          reasoning_mode: detectionForm.value.reasoning_mode,
          analysis_interval_seconds: Number(detectionForm.value.analysis_interval_seconds),
          enabled: detectionForm.value.enabled,
          alarm_cooldown_seconds: Number(detectionForm.value.alarm_cooldown_seconds),
          alarm_thresholds_seconds: thresholdsFor(catalogs.value, detectionForm.value.event_type, detectionForm.value.alarm_thresholds_seconds),
        }
        const url = `v1/vlm-analyzer/cameras/${managedCamera.value.id}/detections`
        await (editingDetection.value ? axios.patch(`${url}/${editingDetection.value.id}`, payload) : axios.post(url, payload))
        detectionModal.value = false
        notify(root.$t('vlm_analyzer.saved'))
        await load()
        managedCamera.value = cameras.value.find(camera => camera.id === managedCamera.value.id) || managedCamera.value
      } catch (requestError) { notify(requestError?.response?.data?.message || root.$t('vlm_analyzer.save_error'), 'danger') } finally { saving.value = false }
    }
    const syncDetection = updatedDetection => {
      if (!updatedDetection) return
      cameras.value = cameras.value.map(camera => camera.id === managedCamera.value.id
        ? { ...camera, detections: camera.detections.map(detection => detection.id === updatedDetection.id ? updatedDetection : detection) }
        : camera)
      managedCamera.value = cameras.value.find(camera => camera.id === managedCamera.value.id) || managedCamera.value
    }
    const toggleDetection = async detection => {
      try {
        const response = await axios.patch(`v1/vlm-analyzer/cameras/${managedCamera.value.id}/detections/${detection.id}`, { enabled: !detection.enabled })
        syncDetection(response.data?.data)
        await load()
        managedCamera.value = cameras.value.find(camera => camera.id === managedCamera.value.id) || managedCamera.value
      } catch (requestError) { notify(root.$t('vlm_analyzer.action_error'), 'danger') }
    }
    const analyze = async (camera, detection) => { try { await axios.post(`v1/vlm-analyzer/cameras/${camera.id}/detections/${detection.id}/analyze`); notify(root.$t('vlm_analyzer.manual_analysis')); await load() } catch (requestError) { notify(root.$t('vlm_analyzer.action_error'), 'danger') } }
    const revoke = url => { if (url) window.URL.revokeObjectURL(url) }
    const clearSnapshot = () => { revoke(snapshotImage.value); snapshotImage.value = ''; snapshotError.value = '' }
    const showSnapshot = async (camera, detection) => {
      const analysis = latestAnalysis(detection)
      if (!analysis) return
      snapshotModal.value = true
      snapshotError.value = ''
      try {
        const response = await axios.get(`v1/vlm-analyzer/cameras/${camera.id}/detections/${detection.id}/analyses/${analysis.id}/snapshot`, { responseType: 'blob' })
        snapshotImage.value = window.URL.createObjectURL(response.data)
      } catch (requestError) { snapshotError.value = root.$t('vlm_analyzer.action_error') }
    }
    let timer
    watch(() => props.section, load)
    watch(() => cameraForm.value.proxy_id, proxyId => {
      const option = cameraOptions.value.find(item => String(item.id) === String(proxyId))
      if (option) selectedCameraOptions.value = [option]
      if (editingCamera.value || cameraForm.value.name) return
      cameraForm.value.name = option?.camera?.name || option?.camera?.camera_name || ''
    })
    onMounted(async () => {
      try {
        await loadCatalogs()
        await load()
      } catch (requestError) {
        error.value = requestError?.response?.data?.message || root.$t('vlm_analyzer.load_error')
      }
      timer = window.setInterval(() => { if (!isCameras.value) load() }, 5000)
    })
    onBeforeUnmount(() => { window.clearInterval(timer); clearSnapshot() })
    return { analyticsModal, analyze, availableEvents, canAnalyze, canCreate, canDelete, canUpdate, cameraFields, cameraForm, cameraModal, cameraThresholdCategories, cameras, clearSnapshot, cooldownLimits, detectionEventOptions, detectionForm, detectionModal, editingCamera, editingDetection, error, eventLabel, eventOptions, filters, formatDate, intervalLimits, isCameras, languageOptions, latestAnalysis, latestResult, load, loading, managedCamera, monitoring, monitoringFields, openAnalytics, openCamera, openDetection, pageTitle, providerOptions, reasoningLabel, reasoningOptions, rememberCameraOptions, removeCamera, resetCameraReasoning, resetReasoning, resetThresholds, saveCamera, saveDetection, saving, selectedCameraOptions, showSnapshot, snapshotError, snapshotImage, snapshotModal, syncDetection, thresholdCategories, thresholdLabel, thresholdLimits, toggleDetection }
  },
}
</script>

<style lang="scss" scoped>
.vlm-analyzer .table td { vertical-align: middle; }
</style>
